/** @module @liquidefy/code-editor/git/git-commit @version 1.0.0 @license LQD Research LLC */

import type {
  EditorResult,
  GitCommit,
  GitPerson,
  GitBlameLine,
  GitBlameResult,
  GitBisectState,
  CommitHash,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  asCommitHash,
  now,
} from '../../code-editor.js';

// ═══ §1 IMPORTS — re-exported above ═══

// ═══ §2 COMMIT PARSING — parse log output into structured commits ═══

export function parseCommitLine(raw: string): EditorResult<GitCommit> {
  // Expected format: hash|parentHashes|authorName|authorEmail|authorDate|committerName|committerEmail|committerDate|subject
  const parts = raw.split('|');
  if (parts.length < 9) return err('INVALID_COMMIT_LINE', 'Commit line has too few fields');
  const [hash, parents, aName, aEmail, aDate, cName, cEmail, cDate, ...messageParts] = parts;
  const message = messageParts.join('|');
  const authorDate = parseInt(aDate!, 10) || 0;
  const committerDate = parseInt(cDate!, 10) || 0;
  const commit: GitCommit = {
    hash: asCommitHash(hash!.trim()),
    parentHashes: parents!.trim() ? parents!.trim().split(' ').map((h) => asCommitHash(h)) : [],
    author: { name: aName!.trim(), email: aEmail!.trim(), date: authorDate },
    committer: { name: cName!.trim(), email: cEmail!.trim(), date: committerDate },
    message: message.trim(),
    date: authorDate,
    isSigned: false,
  };
  return ok(commit, 'COMMIT_PARSED', `Parsed commit ${hash!.trim().slice(0, 7)}`);
}

export function parseLogOutput(output: string): EditorResult<GitCommit[]> {
  const lines = output.split('\n').filter((l) => l.trim().length > 0);
  const commits: GitCommit[] = [];
  for (const line of lines) {
    const result = parseCommitLine(line);
    if (result.success) commits.push(result.value);
  }
  return ok(commits, 'LOG_PARSED', `Parsed ${commits.length} commits`);
}

export function parseShortLog(output: string): EditorResult<{ author: string; count: number }[]> {
  const lines = output.split('\n').filter((l) => l.trim().length > 0);
  const entries: { author: string; count: number }[] = [];
  for (const line of lines) {
    const match = line.trim().match(/^\s*(\d+)\s+(.+)$/);
    if (match) {
      entries.push({ author: match[2]!.trim(), count: parseInt(match[1]!, 10) });
    }
  }
  entries.sort((a, b) => b.count - a.count);
  return ok(entries, 'SHORTLOG_PARSED', `Parsed shortlog: ${entries.length} authors`);
}

export function commitSummary(commit: GitCommit): string {
  const hash = (commit.hash as string).slice(0, 7);
  const firstLine = commit.message.split('\n')[0];
  return `${hash} ${firstLine}`;
}

export function formatCommitDate(timestamp: number): string {
  const d = new Date(timestamp * 1000);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export function isValidCommitHash(hash: string): boolean {
  return /^[0-9a-f]{4,40}$/i.test(hash);
}

export function shortHash(hash: CommitHash, length: number = 7): string {
  return (hash as string).slice(0, Math.max(4, Math.min(length, 40)));
}

export function isAncestor(commits: GitCommit[], a: CommitHash, b: CommitHash): boolean {
  const visited = new Set<string>();
  const queue: string[] = [b as string];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === (a as string)) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    const commit = commits.find((c) => (c.hash as string) === current);
    if (commit) {
      for (const parent of commit.parentHashes) {
        queue.push(parent as string);
      }
    }
  }
  return false;
}

// ═══ §3 COMMIT ANALYSIS — statistics, grouping, conventional commits ═══

export function commitsByAuthor(commits: GitCommit[]): Map<string, GitCommit[]> {
  const map = new Map<string, GitCommit[]>();
  for (const commit of commits) {
    const key = commit.author.email;
    const existing = map.get(key) ?? [];
    existing.push(commit);
    map.set(key, existing);
  }
  return map;
}

export function commitsByDate(
  commits: GitCommit[],
  interval: 'day' | 'week' | 'month'
): Map<string, GitCommit[]> {
  const map = new Map<string, GitCommit[]>();
  for (const commit of commits) {
    const d = new Date(commit.date * 1000);
    let key: string;
    switch (interval) {
      case 'day':
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        break;
      case 'week': {
        const jan1 = new Date(d.getFullYear(), 0, 1);
        const weekNum = Math.ceil(((d.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
        key = `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
        break;
      }
      case 'month':
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        break;
    }
    const existing = map.get(key) ?? [];
    existing.push(commit);
    map.set(key, existing);
  }
  return map;
}

export function commitFrequency(commits: GitCommit[]): number {
  if (commits.length < 2) return commits.length;
  const sorted = [...commits].sort((a, b) => a.date - b.date);
  const firstDate = sorted[0]!.date;
  const lastDate = sorted[sorted.length - 1]!.date;
  const daysSpan = Math.max(1, (lastDate - firstDate) / 86400);
  return commits.length / daysSpan;
}

export function commitMessageLength(
  commits: GitCommit[]
): { min: number; max: number; avg: number; median: number } {
  if (commits.length === 0) return { min: 0, max: 0, avg: 0, median: 0 };
  const lengths = commits.map((c) => c.message.length).sort((a, b) => a - b);
  const min = lengths[0]!;
  const max = lengths[lengths.length - 1]!;
  const sum = lengths.reduce((a, b) => a + b, 0);
  const avg = Math.round(sum / lengths.length);
  const mid = Math.floor(lengths.length / 2);
  const median = lengths.length % 2 === 0 ? Math.round((lengths[mid - 1]! + lengths[mid]!) / 2) : lengths[mid]!;
  return { min, max, avg, median };
}

export const CONVENTIONAL_TYPES = [
  'feat', 'fix', 'docs', 'style', 'refactor', 'perf',
  'test', 'build', 'ci', 'chore', 'revert', 'wip',
] as const;

export function conventionalCommitType(message: string): string | undefined {
  const match = message.match(/^(\w+)(\(.+?\))?(!)?:\s/);
  if (!match) return undefined;
  const type = match[1]!.toLowerCase();
  if ((CONVENTIONAL_TYPES as readonly string[]).includes(type)) return type;
  return type;
}

export function isConventionalCommit(message: string): boolean {
  return /^(\w+)(\(.+?\))?(!)?:\s/.test(message);
}

export function commitImpact(commit: GitCommit): 'low' | 'medium' | 'high' {
  const msgLength = commit.message.length;
  const isBreaking = commit.message.includes('BREAKING CHANGE') || commit.message.match(/^\w+(\(.+?\))?!:/);
  if (isBreaking) return 'high';
  const type = conventionalCommitType(commit.message);
  if (type === 'feat' || type === 'refactor') return 'medium';
  if (msgLength > 500) return 'medium';
  return 'low';
}

// ═══ §4 BLAME — parse blame output and analyze attribution ═══

export function parseBlameOutput(output: string): EditorResult<GitBlameLine[]> {
  const lines = output.split('\n').filter((l) => l.trim().length > 0);
  const blameLines: GitBlameLine[] = [];
  let lineNum = 1;
  for (const line of lines) {
    // Expected format: commitHash (author date time tz lineNumber) content
    const match = line.match(/^([0-9a-f]+)\s+\((.+?)\s+(\d{4}-\d{2}-\d{2})\s+\d{2}:\d{2}:\d{2}\s+[+-]\d{4}\s+(\d+)\)\s?(.*)$/);
    if (match) {
      const dateStr = match[3]!;
      const dateTimestamp = Math.floor(new Date(dateStr).getTime() / 1000);
      blameLines.push({
        lineNumber: parseInt(match[4]!, 10),
        commitHash: asCommitHash(match[1]!),
        author: match[2]!.trim(),
        date: dateTimestamp,
        content: match[5]!,
      });
    } else {
      // Fallback: try simpler format hash|author|date|content
      const simple = line.split('|');
      if (simple.length >= 4) {
        blameLines.push({
          lineNumber: lineNum,
          commitHash: asCommitHash(simple[0]!.trim()),
          author: simple[1]!.trim(),
          date: parseInt(simple[2]!.trim(), 10) || 0,
          content: simple.slice(3).join('|'),
        });
      }
    }
    lineNum++;
  }
  return ok(blameLines, 'BLAME_PARSED', `Parsed ${blameLines.length} blame lines`);
}

export function blameForLine(blameResult: GitBlameResult, lineNumber: number): GitBlameLine | undefined {
  return blameResult.lines.find((l) => l.lineNumber === lineNumber);
}

export function blameAuthors(blameResult: GitBlameResult): string[] {
  const authors = new Set<string>();
  for (const line of blameResult.lines) {
    authors.add(line.author);
  }
  return Array.from(authors);
}

export function blameAge(blameLine: GitBlameLine, nowTimestamp: number): number {
  return Math.max(0, nowTimestamp - blameLine.date);
}

export function oldestBlameLine(blameResult: GitBlameResult): GitBlameLine | undefined {
  if (blameResult.lines.length === 0) return undefined;
  let oldest = blameResult.lines[0];
  for (const line of blameResult.lines) {
    if (line.date < oldest!.date) oldest = line;
  }
  return oldest;
}

export function newestBlameLine(blameResult: GitBlameResult): GitBlameLine | undefined {
  if (blameResult.lines.length === 0) return undefined;
  let newest = blameResult.lines[0];
  for (const line of blameResult.lines) {
    if (line.date > newest!.date) newest = line;
  }
  return newest;
}

export function blameSummary(
  blameResult: GitBlameResult
): { authors: string[]; dateRange: { oldest: number; newest: number }; commitCount: number } {
  const authors = blameAuthors(blameResult);
  const commits = new Set<string>();
  let oldest = Infinity;
  let newest = 0;
  for (const line of blameResult.lines) {
    commits.add(line.commitHash as string);
    if (line.date < oldest) oldest = line.date;
    if (line.date > newest) newest = line.date;
  }
  return {
    authors,
    dateRange: { oldest: oldest === Infinity ? 0 : oldest, newest },
    commitCount: commits.size,
  };
}

// ═══ §5 BISECT — binary search for bug-introducing commit ═══

export function startBisect(good: CommitHash, bad: CommitHash): EditorResult<GitBisectState> {
  if ((good as string) === (bad as string)) {
    return err('SAME_COMMITS', 'Good and bad commits must be different');
  }
  const state: GitBisectState = {
    status: 'active',
    good: [good],
    bad: [bad],
    current: undefined,
    remaining: 0,
  };
  return ok(state, 'BISECT_STARTED', 'Started bisect');
}

export function markGood(state: GitBisectState, hash: CommitHash): EditorResult<GitBisectState> {
  if (state.status !== 'active') return err('BISECT_NOT_ACTIVE', 'No active bisect session');
  const alreadyGood = state.good.some((g) => (g as string) === (hash as string));
  if (alreadyGood) return err('ALREADY_MARKED', `Commit ${hash} already marked as good`);
  const good = [...state.good, hash];
  const remaining = estimateRemaining({ ...state, good });
  return ok(
    { ...state, good, remaining },
    'BISECT_GOOD',
    `Marked ${(hash as string).slice(0, 7)} as good`
  );
}

export function markBad(state: GitBisectState, hash: CommitHash): EditorResult<GitBisectState> {
  if (state.status !== 'active') return err('BISECT_NOT_ACTIVE', 'No active bisect session');
  const alreadyBad = state.bad.some((b) => (b as string) === (hash as string));
  if (alreadyBad) return err('ALREADY_MARKED', `Commit ${hash} already marked as bad`);
  const bad = [...state.bad, hash];
  const remaining = estimateRemaining({ ...state, bad });
  return ok(
    { ...state, bad, remaining },
    'BISECT_BAD',
    `Marked ${(hash as string).slice(0, 7)} as bad`
  );
}

export function bisectStep(state: GitBisectState): EditorResult<GitBisectState> {
  if (state.status !== 'active') return err('BISECT_NOT_ACTIVE', 'No active bisect session');
  const totalMarked = state.good.length + state.bad.length;
  if (totalMarked < 2) return err('NEED_MORE_DATA', 'Need at least one good and one bad commit');
  const remaining = estimateRemaining(state);
  if (remaining <= 0) {
    const foundHash = state.bad[state.bad.length - 1];
    return ok(
      { ...state, status: 'found' as const, current: foundHash, remaining: 0 },
      'BISECT_FOUND',
      `Found culprit: ${(foundHash as string).slice(0, 7)}`
    );
  }
  return ok(
    { ...state, remaining },
    'BISECT_STEP',
    `Bisect step: ~${remaining} remaining`
  );
}

export function estimateRemaining(state: GitBisectState): number {
  const totalMarked = state.good.length + state.bad.length;
  if (totalMarked < 2) return 0;
  return Math.max(0, Math.ceil(Math.log2(totalMarked + 1)) - 1);
}

export function completeBisect(
  state: GitBisectState,
  foundHash: CommitHash
): EditorResult<GitBisectState> {
  return ok(
    { ...state, status: 'found' as const, current: foundHash, remaining: 0 },
    'BISECT_COMPLETE',
    `Bisect found culprit: ${(foundHash as string).slice(0, 7)}`
  );
}

export function resetBisect(state: GitBisectState): EditorResult<GitBisectState> {
  return ok(
    { status: 'inactive', good: [], bad: [], current: undefined, remaining: 0 },
    'BISECT_RESET',
    'Bisect session reset'
  );
}

export function isBisectActive(state: GitBisectState): boolean {
  return state.status === 'active';
}

export function bisectProgress(state: GitBisectState): number {
  if (state.status === 'inactive') return 0;
  if (state.status === 'found') return 1;
  const totalMarked = state.good.length + state.bad.length;
  if (totalMarked < 2) return 0;
  const maxSteps = Math.ceil(Math.log2(totalMarked + 10));
  const stepsDone = totalMarked - 2;
  return Math.min(1, stepsDone / Math.max(1, maxSteps));
}

// ═══ §6 COMMIT MESSAGE — formatting, validation, generation, trailers ═══

export function formatCommitMessage(message: string, maxWidth: number = 72): string {
  const lines = message.split('\n');
  const wrapped: string[] = [];
  for (const line of lines) {
    if (line.length <= maxWidth) {
      wrapped.push(line);
      continue;
    }
    const words = line.split(' ');
    let current = '';
    for (const word of words) {
      if (current.length === 0) {
        current = word;
      } else if (current.length + 1 + word.length <= maxWidth) {
        current += ' ' + word;
      } else {
        wrapped.push(current);
        current = word;
      }
    }
    if (current.length > 0) wrapped.push(current);
  }
  return wrapped.join('\n');
}

export interface CommitMessageRules {
  readonly maxSubjectLength?: number;
  readonly maxBodyLineLength?: number;
  readonly requireConventional?: boolean;
  readonly requireBody?: boolean;
  readonly allowedTypes?: string[];
}

export function validateCommitMessage(
  message: string,
  rules?: CommitMessageRules
): EditorResult<boolean> {
  const errors: string[] = [];
  const lines = message.split('\n');
  const subject = lines[0] ?? '';
  const maxSubject = rules?.maxSubjectLength ?? 72;
  if (subject.length === 0) {
    errors.push('Subject line is empty');
  }
  if (subject.length > maxSubject) {
    errors.push(`Subject exceeds ${maxSubject} characters (${subject.length})`);
  }
  if (subject.endsWith('.')) {
    errors.push('Subject should not end with a period');
  }
  if (rules?.requireConventional && !isConventionalCommit(message)) {
    errors.push('Commit must follow conventional format (type: subject)');
  }
  if (rules?.requireBody && lines.length < 3) {
    errors.push('Commit body is required');
  }
  if (lines.length > 1 && lines[1]!.trim() !== '') {
    errors.push('Second line should be blank (separates subject from body)');
  }
  const maxBodyLine = rules?.maxBodyLineLength ?? 100;
  for (let i = 2; i < lines.length; i++) {
    if (lines[i]!.length > maxBodyLine) {
      errors.push(`Body line ${i + 1} exceeds ${maxBodyLine} characters`);
    }
  }
  if (rules?.requireConventional && rules?.allowedTypes) {
    const type = conventionalCommitType(message);
    if (type && !rules.allowedTypes.includes(type)) {
      errors.push(`Type "${type}" not in allowed types: ${rules.allowedTypes.join(', ')}`);
    }
  }
  if (errors.length > 0) {
    return err('INVALID_COMMIT_MESSAGE', errors.join('; '));
  }
  return ok(true, 'COMMIT_MESSAGE_VALID', 'Commit message is valid');
}

export function generateCommitMessage(
  stagedFiles: string[],
  diff: string
): EditorResult<string> {
  if (stagedFiles.length === 0) return err('NO_STAGED_FILES', 'No staged files to generate message for');
  const extensions = new Set<string>();
  for (const f of stagedFiles) {
    const ext = f.split('.').pop() ?? '';
    if (ext) extensions.add(ext);
  }
  const addCount = (diff.match(/^\+[^+]/gm) ?? []).length;
  const delCount = (diff.match(/^-[^-]/gm) ?? []).length;
  let type = 'chore';
  if (addCount > delCount * 3) type = 'feat';
  else if (delCount > addCount * 3) type = 'refactor';
  else if (addCount > 0 && delCount > 0) type = 'fix';
  const scope = stagedFiles.length === 1
    ? stagedFiles[0]!.split('/').slice(-1)[0]!.replace(/\.[^.]+$/, '')
    : `${stagedFiles.length} files`;
  const message = `${type}(${scope}): update ${Array.from(extensions).join('/')} code\n\n- ${addCount} additions, ${delCount} deletions\n- Files: ${stagedFiles.join(', ')}`;
  return ok(message, 'MESSAGE_GENERATED', 'Generated commit message');
}

export function parseTrailers(message: string): Map<string, string[]> {
  const trailers = new Map<string, string[]>();
  const lines = message.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]!.trim();
    if (line === '') break;
    const match = line.match(/^([\w-]+):\s+(.+)$/);
    if (match) {
      const key = match[1];
      const value = match[2];
      const existing = trailers.get(key!) ?? [];
      existing.push(value!);
      trailers.set(key!, existing);
    } else {
      break;
    }
  }
  return trailers;
}

export function addTrailer(message: string, key: string, value: string): string {
  const lines = message.split('\n');
  const trailer = `${key}: ${value}`;
  if (lines.length === 0) return trailer;
  const lastLine = lines[lines.length - 1]!.trim();
  const isTrailer = /^[\w-]+:\s+.+$/.test(lastLine);
  if (isTrailer) {
    return message + '\n' + trailer;
  }
  if (lastLine === '') {
    return message + trailer;
  }
  return message + '\n\n' + trailer;
}
